import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

/**
 * Slug-to-CTA lookup for journal articles that get a tailored bottom-of-article call
 * to action. Two shapes are supported:
 *
 *   Route-specific (Popular Moves corridor articles):
 *     { province, searchLocation?, headline, subhead }  →  "Get Matched Now!" button
 *     with dest_province + search_location prefill on /get-started.
 *
 *   Generic (evergreen articles like "How to Choose the Right Realtor"):
 *     { generic: true, headline, subhead, buttonText }  →  buttonText (defaults to "Get Matched Now!")
 *     with only a source tag (no province prefill).
 */
const ARTICLE_CTAS = {
    "moving-from-ontario-to-new-brunswick-in-2026-what-buyers-need-to-know": {
        province: "New Brunswick",
        headline: "Ready to move to New Brunswick?",
        subhead:
            "Get personally matched with a vetted New Brunswick agent — no cost, no lead-farm, no pressure.",
    },
    "ontario-to-nova-scotia-the-halifax-move-that-keeps-topping-the-migration-charts": {
        province: "Nova Scotia",
        headline: "Ready to move to Nova Scotia?",
        subhead:
            "Get personally matched with a vetted Halifax-area agent who knows the micro-markets — no cost to you.",
    },
    "bc-to-alberta-in-2026-why-british-columbians-keep-choosing-calgary-and-edmonton": {
        province: "Alberta",
        headline: "Ready to move to Alberta?",
        subhead:
            "Get personally matched with a vetted Calgary or Edmonton agent — free service, no lead-farming.",
    },
    "toronto-to-prince-edward-county-the-in-province-move-that-keeps-surprising-gta-sellers":
        {
            province: "Ontario",
            searchLocation: "Prince Edward County, Ontario",
            headline: "Ready to move to Prince Edward County?",
            subhead:
                "Get personally matched with a vetted PEC-based agent who knows septic, wells, STR rules, and the waterfront — before you fall in love with the wrong house.",
        },
    "how-to-choose-the-right-realtor-in-canada-what-to-look-for-and-questions-to-ask": {
        generic: true,
        headline: "Ready to find your match?",
        subhead:
            "Skip the guessing. Have Thom personally match you with a vetted Realtor in your target market — someone whose style, experience, and approach fit you.",
        buttonText: "Get Matched Now!",
    },
};

export function getRouteArticleMeta(slug) {
    return ARTICLE_CTAS[slug] || null;
}

export default function RouteArticleCta({ slug }) {
    const meta = getRouteArticleMeta(slug);
    if (!meta) return null;

    const params = new URLSearchParams();
    if (meta.province) params.set("dest_province", meta.province);
    if (meta.searchLocation) params.set("search_location", meta.searchLocation);
    params.set("source", `journal:${slug}`);
    const ctaHref = `/get-started?${params.toString()}`;

    const buttonText = meta.buttonText || "Get Matched Now!";

    return (
        <motion.aside
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.55 }}
            data-testid={`route-article-cta-${slug}`}
            className="mt-16 border border-brand-line bg-brand-sand/50 p-8 lg:p-10"
        >
            <div className="text-xs uppercase tracking-[0.25em] text-brand-gold font-semibold mb-4">
                — Get matched
            </div>
            <h3 className="font-serif text-2xl lg:text-3xl text-brand-ink leading-tight mb-4">
                {meta.headline}
            </h3>
            <p className="text-brand-muted leading-relaxed mb-8 max-w-2xl">
                {meta.subhead}
            </p>
            <Link
                to={ctaHref}
                data-testid={`route-article-cta-button-${slug}`}
                className="inline-flex items-center gap-3 bg-brand-ink text-brand-paper px-7 py-3.5 text-sm tracking-wide hover:bg-black transition-all hover:gap-4"
            >
                {buttonText}
                <ArrowRight size={16} strokeWidth={2} />
            </Link>
            <p className="mt-5 text-xs text-brand-muted max-w-xl">
                No cost. No lead auction. Just a short intake and a personal
                introduction from Thom.
            </p>
        </motion.aside>
    );
}
